import type { LandingSection, QuizDefinition, QuizQuestion, StartConfig } from "@quiz/quiz-core";
import { renderEmbeddedQuestion } from "../components/EmbeddedQuestion";
import { preloadCampaignMedia, renderCampaignMedia } from "../components/CampaignMedia";
import { prepareComicQuestionAssets } from "../components/ComicQuestionAssets";
import { getQuizPresentation } from "@quiz/quiz-definitions";
import type { ResolvedUiCopy } from "../ui/copy";
import { button, el } from "../utils";
import { ArrowRight, BookOpen, createElement } from "lucide";

export interface NativeStartOptions {
  quiz: QuizDefinition;
  copy: ResolvedUiCopy;
  firstQuestion?: QuizQuestion;
  selectedAnswerId?: string;
  progress?: number;
  onStart: () => void;
  onAnswer: (answerId: string, button: HTMLButtonElement) => void;
}

export function renderNativeStartScreen(options: NativeStartOptions): HTMLElement {
  if (getQuizPresentation(options.quiz).comicQuest) return renderComicCoverStartScreen(options);
  if (getQuizPresentation(options.quiz).entryComposition === "offer-cards") return renderOfferCardsStartScreen(options);
  const start = options.quiz.start;
  const mode = start.mode ?? "cta";
  const screen = el("main", `screen start-screen start-screen--${start.layout} start-screen--${mode}`);
  screen.dataset.quiz = options.quiz.slug;
  const hero = el("section", "start-hero");
  if (start.background?.type === "color") hero.style.setProperty("--hero-background", start.background.value);

  const masthead = el("header", "start-hero__masthead");
  if (start.logo?.enabled && start.logo.src) {
    const logo = el("img", "start-hero__logo") as HTMLImageElement;
    logo.src = start.logo.src;
    logo.alt = start.logo.alt ?? options.quiz.metadata.name;
    masthead.append(logo);
  }
  masthead.append(el("span", "start-hero__brand", options.quiz.metadata.name));
  if (start.note) masthead.append(el("p", "start-hero__note", start.note));
  const copy = el("div", "start-hero__copy");
  if (start.eyebrow) copy.append(el("p", "eyebrow", start.eyebrow));
  copy.append(el("h1", "start-hero__title", start.title));
  if (start.subtitle) copy.append(el("p", "start-hero__subtitle", start.subtitle));

  const side = el("div", "start-hero__side");
  const mascot = renderCampaignMedia(getQuizPresentation(options.quiz).mascot?.hero, "hero-mascot");
  if (mascot) {
    side.classList.add("start-hero__side--mascot");
    side.append(mascot);
  }
  if (mode === "first-question" && options.firstQuestion) {
    side.append(
      renderEmbeddedQuestion({
        quiz: options.quiz,
        question: options.firstQuestion,
        selectedAnswerId: options.selectedAnswerId,
        progress: options.progress,
        visual: options.firstQuestion.answers.some((answer) => answer.media),
        onAnswer: options.onAnswer
      })
    );
  } else {
    copy.append(button("primary-button", start.cta.label, options.onStart));
    const media = renderStartMedia(start);
    if (media) side.append(media);
  }

  if (getQuizPresentation(options.quiz).entryComposition === "compact-cluster") {
    const cluster = el("div", "start-hero__cluster");
    cluster.append(masthead, copy, side);
    hero.append(cluster);
  } else hero.append(masthead, copy, side);
  screen.append(hero);

  if (options.quiz.landing?.enabled) {
    const landing = el("section", "landing-sections");
    landing.append(...options.quiz.landing.sections.map((section) => renderLandingSection(section, options.onStart)));
    screen.append(landing);
  }

  return screen;
}

function renderOfferCardsStartScreen(options: NativeStartOptions): HTMLElement {
  const screen = el("main", "screen start-screen offer-start-screen");
  screen.dataset.quiz = options.quiz.slug;
  const hero = el("section", "offer-start__hero");
  const brand = el("header", "offer-start__brand");
  if (options.quiz.start.logo?.src) {
    const logo = el("img", "offer-start__logo") as HTMLImageElement;
    logo.src = options.quiz.start.logo.src;
    logo.alt = options.quiz.start.logo.alt ?? options.quiz.metadata.name;
    logo.fetchPriority = "high";
    brand.append(logo);
  } else brand.append(el("strong", "", options.quiz.metadata.name));
  brand.append(el("span", "offer-start__step", `1 / ${getQuizPresentation(options.quiz).counter?.totalSteps ?? options.quiz.questions.length}`));
  hero.append(brand, el("h1", "offer-start__title", options.quiz.start.title));
  for (const paragraph of options.quiz.start.subtitle?.split(/\n\s*\n/) ?? []) {
    hero.append(el("p", "offer-start__description", paragraph));
  }

  const content = el("section", "offer-start__content");
  if (options.quiz.start.eyebrow) content.append(el("p", "offer-start__eyebrow", options.quiz.start.eyebrow));
  if (options.firstQuestion) {
    content.append(renderEmbeddedQuestion({
      quiz: options.quiz,
      question: options.firstQuestion,
      selectedAnswerId: options.selectedAnswerId,
      onAnswer: options.onAnswer
    }));
  }
  if (options.quiz.start.note) content.append(el("p", "offer-start__note", options.quiz.start.note));
  screen.append(hero, content);
  return screen;
}

function renderComicCoverStartScreen(options: NativeStartOptions): HTMLElement {
  const comic = getQuizPresentation(options.quiz).comicQuest!;
  const screen = el("main", "screen comic-cover-screen");
  screen.dataset.quiz = options.quiz.slug;
  const canvas = el("section", "comic-cover");
  if (comic.coverCopy) canvas.classList.add("comic-cover--illustrated");
  const mediaSrc = window.matchMedia("(max-width: 600px)").matches
    ? options.quiz.start.media?.mobileSrc ?? options.quiz.start.media?.src ?? options.quiz.start.media?.desktopSrc ?? ""
    : options.quiz.start.media?.desktopSrc ?? options.quiz.start.media?.src ?? options.quiz.start.media?.mobileSrc ?? "";
  const foreground = el("div", "comic-cover__foreground");
  const image = el("img", "comic-cover__image") as HTMLImageElement;
  image.src = mediaSrc;
  image.alt = "";
  image.width = 1536;
  image.height = 2532;
  image.decoding = "async";
  image.fetchPriority = "high";
  const action = button("comic-cover__action", options.quiz.start.cta.label, options.onStart);
  action.setAttribute("aria-label", options.quiz.start.cta.label);
  if (comic.coverCopy) {
    const brand = el("header", "comic-cover__brand");
    const logo = el("img") as HTMLImageElement;
    logo.src = comic.logoSrc;
    logo.alt = options.quiz.metadata.name;
    brand.append(logo);
    const copy = el("div", "comic-cover__copy");
    copy.append(
      el("h1", "comic-cover__title", comic.coverCopy.title),
      el("p", "comic-cover__message", comic.coverCopy.message),
      el("p", "comic-cover__description", comic.coverCopy.description)
    );
    const benefit = el("p", "comic-cover__benefit");
    benefit.append(createElement(BookOpen, { width: 24, height: 24, "aria-hidden": "true" }), el("span", "", comic.coverCopy.benefit));
    action.append(createElement(ArrowRight, { width: 20, height: 20, "aria-hidden": "true" }));
    foreground.append(brand, copy, benefit);
  }
  foreground.append(image, action);
  canvas.append(foreground);
  screen.append(canvas);
  preloadCampaignMedia(options.firstQuestion?.visual);
  if (options.firstQuestion) void prepareComicQuestionAssets(options.quiz, options.firstQuestion);
  return screen;
}

export function renderStartMedia(start: StartConfig): HTMLElement | undefined {
  if (!start.media) return undefined;
  const wrap = el("div", "start-hero__media");
  const picture = el("picture");
  if (start.media.mobileSrc) {
    const mobile = document.createElement("source");
    mobile.srcset = start.media.mobileSrc;
    mobile.media = "(max-width: 640px)";
    picture.append(mobile);
  }
  const img = el("img") as HTMLImageElement;
  img.src = start.media.desktopSrc ?? start.media.src ?? start.media.mobileSrc ?? "";
  img.alt = start.media.alt;
  img.decoding = "async";
  img.fetchPriority = "high";
  picture.append(img);
  wrap.append(picture);
  return wrap;
}

function renderLandingSection(section: LandingSection, onStart: () => void): HTMLElement {
  const wrap = el("div", `landing-section landing-section--${section.type}`);
  if (section.type === "benefits") {
    if (section.title) wrap.append(el("h2", "section-title", section.title));
    const grid = el("div", "benefit-grid");
    grid.append(
      ...section.items.map((item) => {
        const node = el("article", "benefit-card");
        node.append(el("h3", "", item.title), el("p", "", item.text));
        return node;
      })
    );
    wrap.append(grid);
  } else if (section.type === "text") {
    wrap.append(el("h2", "section-title", section.title), el("p", "", section.text));
  } else if (section.type === "trust") {
    const row = el("div", "trust-row");
    row.append(...section.items.map((item) => el("span", "trust-pill", item)));
    wrap.append(row);
  } else if (section.type === "cta") {
    if (section.text) wrap.append(el("p", "", section.text));
    wrap.append(button("primary-button primary-button--dark", section.label, onStart));
  } else if (section.type === "image") {
    const img = el("img") as HTMLImageElement;
    img.src = section.src;
    img.alt = section.alt;
    img.loading = "lazy";
    wrap.append(img);
  } else if (section.type === "hero") {
    const copy = el("div", "landing-hero__copy");
    copy.append(el("h2", "section-title", section.title));
    if (section.subtitle) copy.append(el("p", "", section.subtitle));
    copy.append(button("primary-button primary-button--dark", section.ctaLabel, onStart));
    wrap.append(copy);
    if (section.media) {
      const media = renderStartMedia({ enabled: true, layout: "split", cta: { label: "", action: "startQuiz" }, title: "", media: section.media });
      if (media) wrap.append(media);
    }
  }
  return wrap;
}
